import { useState, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Autocomplete,
  IconButton,
  alpha,
  useTheme,
  Collapse,
  CircularProgress,
} from "@mui/material";
import { ChevronDown, ChevronUp, Send, Paperclip, X, FileText, Bold, Italic, Underline } from "lucide-react";
import { sendOrgEmail, fetchOrgActivity } from "../../../../../../../redux/actions/marketingAction";
import { enqueueSnackbar } from "notistack";

const FicheEmail = ({ org, orgId }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [sending, setSending] = useState(false);

  const orgContacts = org?.contacts || [];
  const emailOptions = orgContacts
    .filter((c) => c.email)
    .map((c) => ({
      label: `${[c.first_name, c.last_name].filter(Boolean).join(" ")} <${c.email}>`,
      email: c.email,
    }));

  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [bcc, setBcc] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const editorRef = useRef(null);

  const execFormat = useCallback((command) => {
    document.execCommand(command, false, null);
    editorRef.current?.focus();
  }, []);
  const [attachments, setAttachments] = useState([]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setAttachments((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const handleSend = async () => {
    if (to.length === 0) {
      enqueueSnackbar("Au moins un destinataire requis", { variant: "warning" });
      return;
    }
    if (!subject.trim()) {
      enqueueSnackbar("Objet requis", { variant: "warning" });
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      formData.append("to", to.map((t) => t.email || t).join(","));
      if (cc.length > 0) formData.append("cc", cc.map((c) => c.email || c).join(","));
      if (bcc.length > 0) formData.append("bcc", bcc.map((b) => b.email || b).join(","));
      formData.append("subject", subject);
      formData.append("body", editorRef.current?.innerHTML || "");
      attachments.forEach((file) => formData.append("attachments", file));

      await dispatch(sendOrgEmail(orgId, formData));
      enqueueSnackbar("Email envoyé", { variant: "success" });

      // Reset form
      setTo([]);
      setCc([]);
      setBcc([]);
      setSubject("");
      setBody("");
      if (editorRef.current) editorRef.current.innerHTML = "";
      setAttachments([]);
      setExpanded(false);

      // Refresh activity
      dispatch(fetchOrgActivity(orgId));
    } catch (err) {
      enqueueSnackbar(err.message || "Erreur envoi email", { variant: "error" });
    } finally {
      setSending(false);
    }
  };

  const EmailAutocomplete = ({ label, value, onChange }) => (
    <Autocomplete
      multiple
      freeSolo
      options={emailOptions}
      getOptionLabel={(o) => o.label || o.email || o}
      value={value}
      onChange={(_, newVal) => onChange(newVal)}
      renderTags={(tagVal, getTagProps) =>
        tagVal.map((opt, idx) => (
          <Chip
            {...getTagProps({ index: idx })}
            key={idx}
            label={opt.email || opt}
            size="small"
            sx={{ height: 22, fontSize: "0.65rem" }}
          />
        ))
      }
      renderInput={(params) => (
        <TextField {...params} placeholder={label} size="small" sx={{ "& input": { fontSize: "0.8rem" } }} />
      )}
      size="small"
    />
  );

  return (
    <Box>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 2.5, py: 1, cursor: "pointer",
          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.02) },
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: 1, color: theme.palette.text.secondary }}>
          Envoyer un Email
        </Typography>
        {expanded ? <ChevronUp size={14} color={theme.palette.text.disabled} /> : <ChevronDown size={14} color={theme.palette.text.disabled} />}
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ px: 2.5, pb: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          <EmailAutocomplete label="To..." value={to} onChange={setTo} />
          <EmailAutocomplete label="CC..." value={cc} onChange={setCc} />
          <EmailAutocomplete label="CCI (BCC)..." value={bcc} onChange={setBcc} />

          <TextField
            fullWidth
            size="small"
            placeholder="Objet"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            sx={{ "& input": { fontSize: "0.8rem" } }}
          />

          {/* Rich text editor */}
          <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: "8px", overflow: "hidden" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, px: 0.5, py: 0.25, borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.action.hover, 0.3) }}>
              <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); execFormat("bold"); }} sx={{ p: 0.5, borderRadius: "4px" }}>
                <Bold size={14} />
              </IconButton>
              <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); execFormat("italic"); }} sx={{ p: 0.5, borderRadius: "4px" }}>
                <Italic size={14} />
              </IconButton>
              <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); execFormat("underline"); }} sx={{ p: 0.5, borderRadius: "4px" }}>
                <Underline size={14} />
              </IconButton>
            </Box>
            <Box
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              onInput={(e) => setBody(e.currentTarget.innerHTML)}
              sx={{
                minHeight: 100,
                px: 1.5,
                py: 1,
                fontSize: "0.8rem",
                outline: "none",
                "& b, & strong": { fontWeight: 700 },
                "& i, & em": { fontStyle: "italic" },
                "& u": { textDecoration: "underline" },
                "&:empty::before": {
                  content: '"Contenu de l\'email..."',
                  color: theme.palette.text.disabled,
                },
              }}
            />
          </Box>

          {/* Attachments */}
          {attachments.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {attachments.map((file, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1,
                    px: 1, py: 0.5, borderRadius: "6px",
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <FileText size={12} color={theme.palette.text.secondary} />
                  <Typography variant="caption" sx={{ flex: 1, fontSize: "0.7rem" }} noWrap>
                    {file.name}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: "0.6rem", color: theme.palette.text.disabled, flexShrink: 0 }}>
                    {formatSize(file.size)}
                  </Typography>
                  <IconButton size="small" onClick={() => removeAttachment(idx)} sx={{ p: 0.25 }}>
                    <X size={12} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {/* Actions */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <input
              type="file"
              multiple
              ref={fileInputRef}
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
            <Button
              size="small"
              variant="text"
              startIcon={<Paperclip size={14} />}
              onClick={() => fileInputRef.current?.click()}
              sx={{ textTransform: "none", fontSize: "0.75rem", color: theme.palette.text.secondary }}
            >
              Joindre
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <Send size={14} />}
              onClick={handleSend}
              disabled={sending}
              sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.75rem" }}
            >
              Envoyer
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default FicheEmail;
